// src/pages/Auth/AcceptInvite.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';
import { toast } from 'react-toastify';


const AcceptInvite: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get parameters from URL
    let slug = searchParams.get('slug') || '';
    let email = searchParams.get('email') || '';
    let token = searchParams.get('token') || '';

    // ✅ FIX: Clean the token
    if (token) {
        // Remove any newlines, carriage returns, and extra spaces
        token = token.replace(/[\n\r\s]+/g, '');

        // Remove anything after " style=" including the style attribute
        const styleIndex = token.indexOf('" style="');
        if (styleIndex !== -1) {
            token = token.substring(0, styleIndex);
        }

        // Remove any trailing quotes
        token = token.replace(/["']/g, '');

        // Remove any backslashes
        token = token.replace(/\\/g, '');
    }

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        const validateInvite = async () => {
            // Validate required fields
            if (!slug || !email || !token) {
                setError('Invalid invitation link: Missing required parameters');
                setLoading(false);
                return;
            }


            try {
                const response = await fetch(`${BASE_URL}/v1/team/invites/accept`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        token: token,
                        email: email,
                        displayName: '',
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    setIsSuccess(true);

                    // Redirect to signup with pre-filled fields
                    setTimeout(() => {
                        navigate(`/register?email=${encodeURIComponent(email)}&slug=${encodeURIComponent(slug)}&token=${encodeURIComponent(token)}`);
                    }, 1500);
                } else {
                    const errorData = await response.json();
                    setError(errorData.message || 'Invalid or expired invitation link');
                    setLoading(false);
                }
            } catch (err) {
                // console.error('Error validating invite:', err);
                toast.error('Network error. Please try again.');
                setError('Network error. Please try again.');
                setLoading(false);
            }
        };

        validateInvite();
    }, [slug, email, token, navigate]);

    // Handle retry
    const handleRetry = () => {
        setLoading(true);
        setError('');
        window.location.reload();
    };

    // Handle go to login
    const handleGoToLogin = () => {
        navigate('/login');
    };

    // Loading State
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[500px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600 text-lg">Validating your invitation...</p>
                    <p className="text-sm text-gray-400 mt-2">Please wait while we verify your invite</p>
                </div>
            </div>
        );
    }

    // Success State (will auto-redirect)
    if (isSuccess) {
        return (
            <div className="flex items-center justify-center min-h-[500px]">
                <div className="text-center">
                    <div className="text-green-500 text-5xl mb-4">✅</div>
                    <h2 className="text-2xl font-semibold text-gray-800">Invitation Validated!</h2>
                    <p className="text-gray-600 mt-2">Redirecting you to registration...</p>
                    <div className="mt-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                    </div>
                </div>
            </div>
        );
    }

    // Error State
    return (
        <div className="flex items-center justify-center min-h-[500px]">
            <div className="text-center max-w-md w-full">
                <div className="text-red-500 text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-semibold text-gray-800">Invalid Invitation</h2>
                <p className="text-gray-600 mt-2">{error || 'This invitation link is invalid or has expired.'}</p>

                <div className="mt-6 space-y-3">
                    <button
                        onClick={handleRetry}
                        className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors"
                    >
                        Try Again
                    </button>
                    <button
                        onClick={handleGoToLogin}
                        className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
            <ToastContainer position="top-right" autoClose={4000} />
        </div>
    );
};

export default AcceptInvite;